import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ProjectLayout from "../projects/ProjectLayout";
import OrganizationUsersLayout from "./OrganizationUsersLayout";
import SubscriptionPlansLayout from "./SubscriptionPlansLayout";
import OrganizationPositionsLayout from "./OrganizationPositionsLayout";
import OrganizationSettingsLayout from "./OrganizationSettingsLayout";
import TopBar from "../layouts/TopBar";
import useStore from "../config/store";
import api from '../config/api';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";

const MyOrganizationLayout = () => {
  const [view, setView] = useState("projects");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  // useStore() para valores reactivos
  const { organization, plan } = useStore();

  // useStore.getState() solo para setters y valores no reactivos
  const {
    authToken,
    setOrganization,
    setOrganizationName,
    setProject,
    setProjectName,
    setPlan,
    showAlert,
    user
  } = useStore.getState();

  const { t } = useTranslation();

  useEffect(() => {
    const setupAdminOrganization = async () => {
      // Helper para auto-seleccionar proyecto en cuentas trial y navegar directo a la app
      const autoSelectTrialProject = async (organizationId) => {
        const currentProject = useStore.getState().project;
        if (!currentProject) {
          try {
            const projectsResponse = await api.get(
              `/v2/projects/${organizationId}`,
              { headers: { Authorization: `Bearer ${authToken}` } }
            );
            if (
              projectsResponse.data.response?.message === "All projects were successfully obtained" &&
              projectsResponse.data.response?.data?.length > 0
            ) {
              const firstProject = projectsResponse.data.response.data[0];
              setProject(firstProject.uid);
              setProjectName(firstProject.name);
              // ✅ Navegar a home para salir de la pantalla de selección
              navigate('/', { replace: true });
            }
          } catch (err) {
            console.error("Error al auto-seleccionar proyecto trial:", err);
          }
        } else {
          // Ya tiene proyecto seteado, navegar directo
          navigate('/', { replace: true });
        }
      };

      // Si ya está configurada la organización
      if (organization) {
        if (plan === 'trial') {
          await autoSelectTrialProject(organization);
        }
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(
          "/organizations",
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (
          response.data.response?.message === "The organizations were successfully obtained" &&
          response.data.response?.data?.length > 0
        ) {
          const organizations = response.data.response.data;

          if (organizations.length === 1) {
            const adminOrganization = organizations[0];

            setOrganization(adminOrganization.uid);
            setOrganizationName(adminOrganization.name);
            setPlan(adminOrganization.plan);

            if (adminOrganization.plan === 'trial') {
              await autoSelectTrialProject(adminOrganization.uid);
            }

          } else if (organizations.length === 0) {
            setError(t('myOrganization.noOrganizations'));
          } else {
            const firstOrganization = organizations[0];
            setOrganization(firstOrganization.uid);
            setOrganizationName(firstOrganization.name);
            setPlan(firstOrganization.plan);

            showAlert(
              t('myOrganization.autoSelectedOrganization', { organizationName: firstOrganization.name }),
              "info"
            );
          }
        } else {
          setError(t('myOrganization.couldNotLoadOrganizations'));
        }
      } catch (err) {
        console.error("Error al configurar organización:", err);
        setError(t('myOrganization.loadError'));
        showAlert(t('myOrganization.loadErrorAlert'), "error");
      } finally {
        setLoading(false);
      }
    };

    setupAdminOrganization();
  }, [authToken, user, organization, plan]);

  const handleChange = (_, newView) => {
    if (newView) setView(newView);
  };

  // Mostrar spinner mientras se configura la organización
  if (loading) {
    return (
      <Box sx={{ position: "relative", minHeight: "100vh" }}>
        <TopBar />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100vh - 64px)",
            pt: "64px"
          }}
        >
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body1" color="textSecondary">
            {t('myOrganization.configuring')}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Mostrar error si no se pudo configurar
  if (error) {
    return (
      <Box sx={{ position: "relative", minHeight: "100vh" }}>
        <TopBar />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100vh - 64px)",
            pt: "64px",
            px: 3
          }}
        >
          <Alert severity="error" sx={{ maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              {t('myOrganization.configurationError')}
            </Typography>
            <Typography variant="body2">
              {error}
            </Typography>
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", minHeight: "100vh" }}>
      <TopBar />

      {/* Selector fijo arriba a la derecha */}
      <Paper
        elevation={6}
        sx={{
          position: "absolute",
          top: 72,
          right: 32,
          zIndex: 1300,
          p: "0.5rem 1rem",
          display: "flex",
          alignItems: "center",
          borderRadius: "0.5rem",
        }}
      >
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={handleChange}
          size="small"
        >
          <ToggleButton value="projects">{t('projects.title')}</ToggleButton>
          <ToggleButton value="users">{t('users.title')}</ToggleButton>
          <ToggleButton value="subscription">{t('subscription.title')}</ToggleButton>
          <ToggleButton value="positions">{t('drawer.positions')}</ToggleButton>
          <ToggleButton value="settings">{t('organizations.title')}</ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {/* Vista seleccionada */}
      <Box>
        {view === "projects" ? (
          <ProjectLayout />
        ) : view === "users" ? (
          <OrganizationUsersLayout />
        ) : view === "subscription" ? (
          <SubscriptionPlansLayout />
        ) : view === "positions" ? (
          <OrganizationPositionsLayout />
        ) : view === "settings" ? (
          <OrganizationSettingsLayout />
        ) : (
          <ProjectLayout />
        )}
      </Box>
    </Box>
  );
};

export default MyOrganizationLayout;